import { Journey } from '@audibene/ta.utils-opportunity'
import { useMemo } from 'react'
import { CalendarIntegrationType, teleaudiologyOrTaLightJourneys } from '../../../../constants'
import { useAuth, useFeatureFlags } from 'hooks'
import { useCalendarIntegrations } from 'hooks/useCalendarIntegrations'
import { Recommendation, RecommendationResource } from 'types'

interface UseManualTimeEnabledResource {
  resource?: RecommendationResource
  isLoading: boolean
}

const ALLOWED_INTEGRATION_TYPES = [CalendarIntegrationType.GOOGLE, CalendarIntegrationType.OUTLOOK]
const useManualTimeEnabledResource = (journey: Journey, recommendations?: Recommendation): UseManualTimeEnabledResource => {
  const resource = recommendations?.providers?.[0]?.resource
  const [shouldShowManualTimeButton] = useFeatureFlags<boolean>(['SHOW_MANUALLY_ADD_TIME_BUTTON'])
  const { calendarIntegrations, isLoading: areCalendarIntegrationsLoading } = useCalendarIntegrations(resource?.id)
  const { user, isLoading: isUserLoading } = useAuth()

  const canResourceUseManualTime = useMemo(() => {
    const providers = recommendations?.providers
    if (
      !shouldShowManualTimeButton ||
      !providers ||
      providers.length !== 1 ||
      !calendarIntegrations ||
      !teleaudiologyOrTaLightJourneys.has(journey) ||
      !user ||
      calendarIntegrations.length === 0
    ) {
      return false
    }

    const { provider } = providers[0]

    if (provider?.partnerEmployeeId !== user?.account_id) {
      return false
    }

    return calendarIntegrations.every((integration) => ALLOWED_INTEGRATION_TYPES.includes(integration.type))
  }, [shouldShowManualTimeButton, recommendations, user, journey, calendarIntegrations])

  if (isUserLoading || areCalendarIntegrationsLoading) {
    return {
      isLoading: true,
    }
  }

  return {
    resource: canResourceUseManualTime ? resource : undefined,
    isLoading: false,
  }
}

export { useManualTimeEnabledResource }
